import { QueryClient } from '@tanstack/react-query';

import { API_URLS } from '@/shared/api/main';

import { IBookmarkFoldersCache } from '@/shared/types/bookmarkFolder';

/**
 * Ключ списка папок избранного. `userId` обязателен: после смены аккаунта React Query иначе
 * отдаст папки предыдущего пользователя (мультитенантность).
 * Живёт в shared, а не в entities/bookmark-folder: ключ и его инвалидация — общий контракт,
 * которым пользуются мутации разных entity (castings, user), а кросс-импорт entity→entity
 * запрещён FSD-границами.
 * @param userId - id текущего пользователя
 */
export const getBookmarkFoldersQueryKey = (userId: number | undefined) => [
  API_URLS.bookmarkFolders.list,
  userId
];

/**
 * Инвалидация списка папок: счётчики в блоке, ленте и пикере — проекции одного факта
 * @param queryClient - экземпляр QueryClient
 * @param userId - id текущего пользователя
 */
export const invalidateBookmarkFolders = (
  queryClient: QueryClient,
  userId: number | undefined
): void => {
  queryClient.invalidateQueries({
    queryKey: getBookmarkFoldersQueryKey(userId)
  });
};

/** Патч синхронной правки счётчиков папок избранного */
export interface IBookmarkFolderCountsPatch {
  /** Папки, чей `items_count` меняется */
  folderIds: number[];
  /** На сколько меняется `items_count` каждой из `folderIds` */
  itemsDelta: 1 | -1;
  /** На сколько меняется `total_bookmarks` (0 — общее избранное не изменилось) */
  totalBookmarksDelta: 0 | 1 | -1;
}

/**
 * Синхронная правка счётчиков папок ДО инвалидации: кнопка папки/сердце на карточке
 * красятся сразу по ответу мутации, а счётчики в блоке «Мои папки» без этого догоняли
 * только следующим рефетчем — два момента вместо одного. Сервер остаётся источником
 * правды: следом всегда идёт `invalidateBookmarkFolders`. Живёт в shared, потому что
 * нужна мутациям трёх entity (bookmark-folder, user, castings)
 * @param queryClient - экземпляр QueryClient
 * @param userId - id текущего пользователя
 * @param patch - какие папки и на сколько
 */
export const patchBookmarkFolderCounts = (
  queryClient: QueryClient,
  userId: number | undefined,
  patch: IBookmarkFolderCountsPatch
): void => {
  // Без generic: spread `...old` под параметром типа TS не сводит обратно к T.
  // Реальный кеш (`IBookmarkFoldersResponse`) шире `IBookmarkFoldersCache`, лишние
  // поля (`name`, `created_at`) переживают spread как есть
  queryClient.setQueryData<IBookmarkFoldersCache>(
    getBookmarkFoldersQueryKey(userId),
    old => {
      if (!old) return old;

      return {
        ...old,
        bookmark_folders: old.bookmark_folders.map(folder =>
          patch.folderIds.includes(folder.id)
            ? {
                ...folder,
                items_count: Math.max(0, folder.items_count + patch.itemsDelta)
              }
            : folder
        ),
        total_bookmarks: Math.max(
          0,
          old.total_bookmarks + patch.totalBookmarksDelta
        )
      };
    }
  );
};
